import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';

import { AppShell } from '@/components/shell/AppShell';
import { NAV_ITEMS } from '@/config/navigation';
import { NotFoundScreen } from '@/screens/NotFoundScreen';
import { GymDetailRoute } from '@/screens/gym-detail/GymDetailRoute';
import { GymsRoute } from '@/screens/gyms/GymsRoute';
import { gymsSearchSchema } from '@/screens/gyms/gyms-search';
import { ActivityRoute } from '@/screens/activity/ActivityRoute';
import { AiOpsRoute } from '@/screens/ai-ops/AiOpsRoute';
import { aiOpsSearchSchema } from '@/screens/ai-ops/ai-ops-search';
import { AiSpendRoute } from '@/screens/ai-spend/AiSpendRoute';
import { aiSpendSearchSchema } from '@/screens/ai-spend/ai-spend-search';
import { ApiConsoleRoute } from '@/screens/api-console/ApiConsoleRoute';
import { apiConsoleSearchSchema } from '@/screens/api-console/api-console-search';
import { activitySearchSchema } from '@/screens/activity/activity-search';
import { BillingRoute } from '@/screens/billing/BillingRoute';
import { billingSearchSchema } from '@/screens/billing/billing-search';
import { BroadcastsRoute } from '@/screens/broadcasts/BroadcastsRoute';
import { CatalogChangesRoute } from '@/screens/catalog-changes/CatalogChangesRoute';
import { catalogChangesSearchSchema } from '@/screens/catalog-changes/catalog-changes-search';
import { broadcastsSearchSchema } from '@/screens/broadcasts/broadcasts-search';
import { WaitTipsRoute } from '@/screens/wait-tips/WaitTipsRoute';
import { FeatureSwitchesRoute } from '@/screens/feature-switches/FeatureSwitchesRoute';
import { RhythmRoute } from '@/screens/rhythm/RhythmRoute';
import { waitTipsSearchSchema } from '@/screens/wait-tips/wait-tips-search';
import { ExerciseDetailRoute } from '@/screens/exercise-detail/ExerciseDetailRoute';
import { NewExerciseRoute } from '@/screens/exercise-detail/NewExerciseRoute';
import { exerciseNewSearchSchema } from '@/screens/exercise-detail/exercise-new-search';
import { exerciseDetailSearchSchema } from '@/screens/exercise-detail/exercise-detail-search';
import { ExercisesRoute } from '@/screens/exercises/ExercisesRoute';
import { exercisesSearchSchema } from '@/screens/exercises/exercises-search';
import { ExerciseRequestsRoute } from '@/screens/exercise-requests/ExerciseRequestsRoute';
import { exerciseRequestsSearchSchema } from '@/screens/exercise-requests/exercise-requests-search';
import { NewFoodRoute } from '@/screens/food-new/NewFoodRoute';
import { foodNewSearchSchema } from '@/screens/food-new/food-new-search';
import { FoodRequestRoute } from '@/screens/food-requests/FoodRequestRoute';
import { FoodRequestsRoute } from '@/screens/food-requests/FoodRequestsRoute';
import { foodRequestsSearchSchema } from '@/screens/food-requests/food-requests-search';
import { GymCatalogRoute } from '@/screens/gym-catalog/GymCatalogRoute';
import { gymCatalogSearchSchema } from '@/screens/gym-catalog/gym-catalog-search';
import { FeedbackRoute } from '@/screens/feedback/FeedbackRoute';
import { feedbackSearchSchema } from '@/screens/feedback/feedback-search';
import { FoodDetailRoute } from '@/screens/food-detail/FoodDetailRoute';
import { foodDetailSearchSchema } from '@/screens/food-detail/food-detail-search';
import { FoodsRoute } from '@/screens/foods/FoodsRoute';
import { foodsSearchSchema } from '@/screens/foods/foods-search';
import { JobsRoute } from '@/screens/jobs/JobsRoute';
import { NotificationsRoute } from '@/screens/notifications/NotificationsRoute';
import { notificationsSearchSchema } from '@/screens/notifications/notifications-search';
import { OverviewRoute } from '@/screens/overview/OverviewRoute';
import { overviewSearchSchema } from '@/screens/overview/overview-search';
import { jobsSearchSchema } from '@/screens/jobs/jobs-search';
import { MealPatternsRoute } from '@/screens/meal-patterns/MealPatternsRoute';
import { mealPatternsSearchSchema } from '@/screens/meal-patterns/meal-patterns-search';
import { PromotionDetailRoute } from '@/screens/promotions/PromotionDetailRoute';
import { PromotionsRoute } from '@/screens/promotions/PromotionsRoute';
import { promotionDetailSearchSchema, promotionsSearchSchema } from '@/screens/promotions/promotions-search';
import { PlanOpsRoute } from '@/screens/plan-ops/PlanOpsRoute';
import { planOpsSearchSchema } from '@/screens/plan-ops/plan-ops-search';
import { UserDetailRoute } from '@/screens/user-detail/UserDetailRoute';
import { userDetailSearchSchema } from '@/screens/user-detail/user-detail-search';
import { UsersRoute } from '@/screens/users/UsersRoute';
import { usersSearchSchema } from '@/screens/users/users-search';

function RootLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundScreen,
});

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.overview.path,
  validateSearch: overviewSearchSchema,
  component: OverviewRoute,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.users.path,
  validateSearch: usersSearchSchema,
  component: UsersRoute,
});

/**
 * A child path rather than its own top-level route, so `screenIdForPath`'s
 * longest-prefix match keeps Users highlighted in the sidebar and rooted in the
 * breadcrumb without registering anything new.
 */
const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.users.path}/$userId`,
  validateSearch: userDetailSearchSchema,
  component: UserDetailRoute,
});

const gymsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.gyms.path,
  validateSearch: gymsSearchSchema,
  component: GymsRoute,
});

/** A child path, for the same reason `userDetailRoute` is one. */
const gymDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.gyms.path}/$gymId`,
  component: GymDetailRoute,
});

const foodsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.foods.path,
  validateSearch: foodsSearchSchema,
  component: FoodsRoute,
});

/**
 * Creating one. A static segment, ranked above any future `$foodId`, and a
 * child of `/foods` so the sidebar's longest-prefix match keeps Food catalog
 * highlighted without registering a second nav entry.
 */
const newFoodRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.foods.path}/new`,
  validateSearch: foodNewSearchSchema,
  component: NewFoodRoute,
});

/**
 * Its own top-level route, sibling of `/foods`, not a third view on it: the
 * catalog screen's two views are filter presets over one endpoint, and this is
 * a different endpoint entirely.
 */
const foodDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  // A child path, not a nested route: `screenIdForPath` matches the longest
  // prefix, so the catalog stays highlighted in the sidebar without this
  // route registering anything.
  path: '/foods/$foodId',
  validateSearch: foodDetailSearchSchema,
  component: FoodDetailRoute,
});

const foodRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.foodrequests.path,
  validateSearch: foodRequestsSearchSchema,
  component: FoodRequestsRoute,
});

/**
 * One request on its own page: a new food or a fix to review, or what an
 * answer wrote. A child path, so the sidebar keeps Food requests highlighted
 * and the top bar offers the way back to the queue.
 */
const foodRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.foodrequests.path}/$requestId`,
  component: FoodRequestRoute,
});

/** Every change to the food and exercise catalog, by what made it, with Revert. */
const catalogChangesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.catalogchanges.path,
  validateSearch: catalogChangesSearchSchema,
  component: CatalogChangesRoute,
});

const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.feedback.path,
  validateSearch: feedbackSearchSchema,
  component: FeedbackRoute,
});

const exercisesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.exercises.path,
  validateSearch: exercisesSearchSchema,
  component: ExercisesRoute,
});

/**
 * Creating one. A static segment, so the router ranks it above
 * `$exerciseId` and "new" is never read as an id.
 */
const newExerciseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.exercises.path}/new`,
  validateSearch: exerciseNewSearchSchema,
  component: NewExerciseRoute,
});

/**
 * Exercises equipment scans found missing from the catalog. Its own top-level
 * route beside the library, as food requests sit beside the food catalog: a
 * queue over a different endpoint, not a filter on the library.
 */
const exerciseRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.exerciserequests.path,
  validateSearch: exerciseRequestsSearchSchema,
  component: ExerciseRequestsRoute,
});

/** "From gyms": Steado's review of what gyms add (Steado for Gyms). */
const gymCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.gymcatalog.path,
  validateSearch: gymCatalogSearchSchema,
  component: GymCatalogRoute,
});

/** A child path, for the same reason `userDetailRoute` is one. */
const exerciseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.exercises.path}/$exerciseId`,
  validateSearch: exerciseDetailSearchSchema,
  component: ExerciseDetailRoute,
});

const planOpsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.planops.path,
  validateSearch: planOpsSearchSchema,
  component: PlanOpsRoute,
});

const mealPatternsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.patterns.path,
  validateSearch: mealPatternsSearchSchema,
  component: MealPatternsRoute,
});

const aiOpsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.aiops.path,
  validateSearch: aiOpsSearchSchema,
  component: AiOpsRoute,
});

const aiSpendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.aispend.path,
  validateSearch: aiSpendSearchSchema,
  component: AiSpendRoute,
});

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.jobs.path,
  validateSearch: jobsSearchSchema,
  component: JobsRoute,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.notifications.path,
  validateSearch: notificationsSearchSchema,
  component: NotificationsRoute,
});

const broadcastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.broadcasts.path,
  validateSearch: broadcastsSearchSchema,
  component: BroadcastsRoute,
});

const waitTipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.waittips.path,
  validateSearch: waitTipsSearchSchema,
  component: WaitTipsRoute,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.billing.path,
  validateSearch: billingSearchSchema,
  component: BillingRoute,
});

const promotionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.promotions.path,
  validateSearch: promotionsSearchSchema,
  component: PromotionsRoute,
});

/** A child path, for the same reason `userDetailRoute` is one. */
const promotionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${NAV_ITEMS.promotions.path}/$campaignId`,
  validateSearch: promotionDetailSearchSchema,
  component: PromotionDetailRoute,
});

const featureSwitchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.switches.path,
  component: FeatureSwitchesRoute,
});

const rhythmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.rhythm.path,
  component: RhythmRoute,
});

const apiConsoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.console.path,
  validateSearch: apiConsoleSearchSchema,
  component: ApiConsoleRoute,
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_ITEMS.activity.path,
  validateSearch: activitySearchSchema,
  component: ActivityRoute,
});

export const routeTree = rootRoute.addChildren([
  overviewRoute,
  usersRoute,
  userDetailRoute,
  gymsRoute,
  gymDetailRoute,
  foodsRoute,
  newFoodRoute,
  foodDetailRoute,
  foodRequestsRoute,
  foodRequestRoute,
  catalogChangesRoute,
  gymCatalogRoute,
  feedbackRoute,
  exercisesRoute,
  newExerciseRoute,
  exerciseDetailRoute,
  exerciseRequestsRoute,
  planOpsRoute,
  mealPatternsRoute,
  aiOpsRoute,
  aiSpendRoute,
  jobsRoute,
  notificationsRoute,
  rhythmRoute,
  broadcastsRoute,
  waitTipsRoute,
  billingRoute,
  promotionsRoute,
  promotionDetailRoute,
  featureSwitchesRoute,
  apiConsoleRoute,
  activityRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
